import { useTranslation } from 'react-i18next';
import { TokenUsageOperationEntry, TokenUsageResponse } from 'features/usage/services/usage-service';
import { UsageViewModel } from 'features/usage/utils/build-usage-view-model';
import { UsageOverview } from './usage-overview';
import { UsageDailySection, UsageOperationSection, UsageProviderSection } from './usage-tables';
import { UsageWhoUsedCard } from './usage-who-used-card';

export function UsageDashboard({
  data,
  viewModel,
  operations,
  showUsers,
  isOwnUsage = false,
  isAdmin = false,
}: {
  data: TokenUsageResponse;
  viewModel: UsageViewModel;
  operations: TokenUsageOperationEntry[];
  showUsers: boolean;
  /** The signed-in person's own usage, so section copy says "your". */
  isOwnUsage?: boolean;
  /** Admins see the footnotes on how turns, model calls and unknown rows are counted. */
  isAdmin?: boolean;
}) {
  const { t } = useTranslation();

  // Figma spaces the usage cards 24px apart, tighter than the 40px page layout gap.
  return (
    <div className="flex flex-col gap-6">
      <UsageOverview
        totals={data.totals}
        periodStart={data.period.start}
        periodEnd={data.period.end}
        periodDays={viewModel.periodDays.length}
        activeDays={data.breakdowns?.daily?.length ?? 0}
        promptShare={viewModel.promptShare}
        completionShare={viewModel.completionShare}
        completionRatio={viewModel.completionRatio}
      />
      <UsageDailySection
        days={viewModel.allDays}
        peakTokens={viewModel.peakTokens}
        isOwnUsage={isOwnUsage}
      />
      <UsageWhoUsedCard
        models={viewModel.orderedModels}
        agents={viewModel.byAgent}
        users={viewModel.byUser}
        showUsers={showUsers}
        isOwnUsage={isOwnUsage}
        totalTokens={data.totals.total_tokens}
      />
      <div className="grid items-stretch gap-x-4 gap-y-6 xl:grid-cols-2">
        <UsageOperationSection
          operations={operations}
          totalTokens={data.totals.total_tokens}
          isOwnUsage={isOwnUsage}
        />
        <UsageProviderSection providers={viewModel.byProvider} isOwnUsage={isOwnUsage} />
      </div>
      {isAdmin && (
        <p className="text-xs leading-6 text-muted-foreground">
          {t('FOOTER_INDEPENDENT_COUNTS')} {t('FOOTER_UNKNOWN_MODEL')}
        </p>
      )}
    </div>
  );
}
